/**
 * The Contacts route search-param schema. Holds the multi-select state
 * (`selectedIds`), the company drill filter (`companyId`), the Buyer
 * auto-select-from-quote context, and the panel/sort bits - all in the URL.
 */

import { EventRecordFilterSchema, FilterExprSchema } from '@hostingportal/schemas'
import { z } from 'zod'
import { PANEL_TYPES } from '../../../shell/panel-state'

const csv = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((v) => {
    if (v == null) return undefined
    const arr = Array.isArray(v) ? v : v.split(',').filter(Boolean)
    return arr.length ? arr : undefined
  })

export const contactsSearchSchema = z.object({
  panel: z.enum(PANEL_TYPES).optional(),
  panelId: z.string().optional(),
  panelMode: z.enum(['create']).optional(),

  // Drill-from-opportunity filter.
  companyId: z.string().optional(),
  states: csv,
  segments: csv,
  contactType: z.string().optional(),
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),

  // Multi-select state (the email-composer handoff payload).
  selectedIds: csv,
  // When opened from a quote context, auto-select Buyer contacts.
  autoSelectBuyerQuote: z.string().optional(),

  sortField: z.string().optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),

  // AND/OR filter expression (Unit B: filter state lives in the URL, Zod-validated).
  filter: FilterExprSchema.optional(),
  // Quick-filter values (the top-bar chips), URL-backed + Zod-validated.
  quick: FilterExprSchema.optional(),
  // "Did event X in the last N days" record filter (honored server-side by contact.list).
  eventFilter: EventRecordFilterSchema.optional(),
})

export type ContactsSearch = z.infer<typeof contactsSearchSchema>
